import React, { useCallback, useMemo } from 'react';
import {
  UpdateCalculationConfigurationPayload,
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
} from '../../../../apis/apiGlobalConfiguration';
import {
  ConfigurationFieldStatus,
  rtkQueryPatchStatusToConfigurationFieldStatus,
} from '@ydistri/utils';
import {
  useDeleteProductListConfigurationMutation,
  useGetProductListConfigurationQuery,
} from '../apiProductLists';
import { SortDirection } from '@ydistri/api-sdk';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';
import TypeOfExceptionButtons from '../../components/TypeOfExceptionButtons';

interface ProductListTypeOfExceptionProps {
  hasException: boolean;
}

const ProductListTypeOfException = ({
  hasException,
}: ProductListTypeOfExceptionProps): React.ReactElement => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();

  const { data: calculationConfiguration, isFetching } = useGetCalculationConfigurationQuery(
    templateOrCalculation.value,
  );
  const { data: productListConfigurations } = useGetProductListConfigurationQuery(
    templateOrCalculation.hasValidValue
      ? {
          templateOrCalculation,
          sortings: [{ fieldName: 'CreatedAt', direction: SortDirection.Desc }],
        }
      : skipToken,
  );
  const allEnabled = calculationConfiguration?.productListsAllEnabled;

  const [updateConfiguration, updateConfigurationStatus] =
    useUpdateCalculationConfigurationMutation();
  const [deleteConfiguration] = useDeleteProductListConfigurationMutation();

  const productListsToReset = useMemo(
    () => productListConfigurations?.filter(pl => pl.isConfigured),
    [productListConfigurations],
  );

  const status = isFetching
    ? ConfigurationFieldStatus.LOADING
    : rtkQueryPatchStatusToConfigurationFieldStatus(updateConfigurationStatus);

  const onChangeHandler = useCallback(
    (value: boolean) => {
      if (templateOrCalculation.hasValidValue && templateOrCalculation.value !== skipToken) {
        const payload: UpdateCalculationConfigurationPayload = {
          templateOrCalculation: templateOrCalculation.value,
          request: {
            productListsAllEnabled: value,
          },
        };

        productListsToReset?.forEach(pl => {
          deleteConfiguration({ templateOrCalculation, productListId: pl.productListId });
        });

        updateConfiguration(payload);
      }
    },
    [deleteConfiguration, productListsToReset, templateOrCalculation, updateConfiguration],
  );

  const onSetAllEnabled = useCallback(() => onChangeHandler(true), [onChangeHandler]);
  const onSetAllDisabled = useCallback(() => onChangeHandler(false), [onChangeHandler]);

  return (
    <TypeOfExceptionButtons
      allEnabled={allEnabled}
      status={status}
      disabled={!(isConfigurationEditable && templateOrCalculation.hasValidValue)}
      templateOrCalculationType={templateOrCalculation.type}
      hasException={hasException}
      onSetAllEnabled={onSetAllEnabled}
      onSetAllDisabled={onSetAllDisabled}
      confirmationTitle="Product lists"
    />
  );
};

export default ProductListTypeOfException;
